'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useSyncExternalStore } from 'react';
import { WordCloudVisualization } from '@/components/molecules/WordCloudVisualization';
import { CATEGORY_LABELS, Category, calculateCategoryStats, categorizeWords } from '@/lib/categorization';
import { analyzeSentiments, calculateSentimentStats } from '@/lib/sentiment';
import { WordFrequency } from '@/lib/wordCloud';
interface BlogWordCloudContentProps {
  wordFrequencies: WordFrequency[];
  totalPosts: number;
}

// Phones get a portrait canvas so the words are not shrunk to fit a wide one
const NARROW_QUERY = '(max-width: 640px)';
const WIDE_CANVAS = {
  width: 1000,
  height: 600
};
const NARROW_CANVAS = {
  width: 520,
  height: 780
};
function subscribeToNarrow(onChange: () => void) {
  const query = window.matchMedia(NARROW_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}
export function BlogWordCloudContent(t0) {
  const $ = _c(88);
  const {
    wordFrequencies,
    totalPosts
  } = t0;
  const [viewMode, setViewMode] = useState("sentiment");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const isNarrow = useSyncExternalStore(subscribeToNarrow, _temp, _temp2);
  const canvas = isNarrow ? NARROW_CANVAS : WIDE_CANVAS;
  let t1;
  if ($[0] !== wordFrequencies) {
    const wordsWithSentiment = analyzeSentiments(wordFrequencies);
    t1 = calculateSentimentStats(wordsWithSentiment);
    $[0] = wordFrequencies;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sentimentStats = t1;
  let t2;
  if ($[2] !== canvas.height || $[3] !== canvas.width || $[4] !== isNarrow || $[5] !== selectedCategory || $[6] !== sentimentStats.negative || $[7] !== sentimentStats.neutral || $[8] !== sentimentStats.positive || $[9] !== sentimentStats.totalNegative || $[10] !== sentimentStats.totalNeutral || $[11] !== sentimentStats.totalPositive || $[12] !== totalPosts || $[13] !== viewMode || $[14] !== wordFrequencies) {
    const wordsWithCategory = categorizeWords(wordFrequencies);
    const categoryStats = calculateCategoryStats(wordsWithCategory);
    const maxCategoryCount = Math.max(1, ...categoryStats.map(_temp3));
    let t3;
    if ($[16] !== sentimentStats.positive || $[17] !== sentimentStats.totalPositive) {
      t3 = {
        key: "positive",
        label: "Positive Words",
        count: sentimentStats.positive,
        total: sentimentStats.totalPositive
      };
      $[16] = sentimentStats.positive;
      $[17] = sentimentStats.totalPositive;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    let t4;
    if ($[19] !== sentimentStats.neutral || $[20] !== sentimentStats.totalNeutral) {
      t4 = {
        key: "neutral",
        label: "Neutral Words",
        count: sentimentStats.neutral,
        total: sentimentStats.totalNeutral
      };
      $[19] = sentimentStats.neutral;
      $[20] = sentimentStats.totalNeutral;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    let t5;
    if ($[22] !== sentimentStats.negative || $[23] !== sentimentStats.totalNegative) {
      t5 = {
        key: "negative",
        label: "Negative Words",
        count: sentimentStats.negative,
        total: sentimentStats.totalNegative
      };
      $[22] = sentimentStats.negative;
      $[23] = sentimentStats.totalNegative;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    let t6;
    if ($[25] !== t3 || $[26] !== t4 || $[27] !== t5) {
      t6 = [t3, t4, t5];
      $[25] = t3;
      $[26] = t4;
      $[27] = t5;
      $[28] = t6;
    } else {
      t6 = $[28];
    }
    const sentiments = t6;
    let t7;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="k">Demo · Text analysis</div>;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    let t8;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <h1 className="md">Blog Word Cloud<span aria-hidden="true">.</span></h1>;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== totalPosts) {
      t9 = <div>{t7}{t8}<p className="lede">A visualization of the most frequently used words across all{" "}{totalPosts} blog posts with sentiment analysis and content categorization</p></div>;
      $[31] = totalPosts;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    let t10;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <dt>Posts analyzed</dt>;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    let t11;
    if ($[34] !== totalPosts) {
      t11 = <div>{t10}<dd>{totalPosts}</dd></div>;
      $[34] = totalPosts;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    let t12;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <dt>Words displayed</dt>;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    let t13;
    if ($[37] !== wordFrequencies.length) {
      t13 = <div>{t12}<dd>{wordFrequencies.length}</dd></div>;
      $[37] = wordFrequencies.length;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    let t14;
    if ($[39] !== sentimentStats.positive || $[40] !== wordFrequencies.length) {
      t14 = wordFrequencies.length > 0 ? (sentimentStats.positive / wordFrequencies.length * 100).toFixed(1) : "0.0";
      $[39] = sentimentStats.positive;
      $[40] = wordFrequencies.length;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    let t15;
    if ($[42] !== t14) {
      t15 = <dt>Positive words ({t14}%)</dt>;
      $[42] = t14;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    let t16;
    if ($[44] !== sentimentStats.positive) {
      t16 = <dd>{sentimentStats.positive}</dd>;
      $[44] = sentimentStats.positive;
      $[45] = t16;
    } else {
      t16 = $[45];
    }
    let t17;
    if ($[46] !== t15 || $[47] !== t16) {
      t17 = <div>{t15}{t16}</div>;
      $[46] = t15;
      $[47] = t16;
      $[48] = t17;
    } else {
      t17 = $[48];
    }
    let t18;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <dt>Categories</dt>;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    const t19 = <div>{t18}<dd>{categoryStats.length}</dd></div>;
    let t20;
    if ($[50] !== t11 || $[51] !== t13 || $[52] !== t17 || $[53] !== t19) {
      t20 = <dl className="stats">{t11}{t13}{t17}{t19}</dl>;
      $[50] = t11;
      $[51] = t13;
      $[52] = t17;
      $[53] = t19;
      $[54] = t20;
    } else {
      t20 = $[54];
    }
    let t21;
    if ($[55] !== t20 || $[56] !== t9) {
      t21 = <header className="head">{t9}{t20}</header>;
      $[55] = t20;
      $[56] = t9;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    let t22;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = () => {
        setViewMode("sentiment");
        setSelectedCategory(null);
      };
      $[58] = t22;
    } else {
      t22 = $[58];
    }
    const t23 = viewMode === "sentiment";
    let t24;
    if ($[59] !== t23) {
      t24 = <button type="button" onClick={t22} className="btn" aria-pressed={t23}>Sentiment Analysis</button>;
      $[59] = t23;
      $[60] = t24;
    } else {
      t24 = $[60];
    }
    let t25;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = () => setViewMode("category");
      $[61] = t25;
    } else {
      t25 = $[61];
    }
    const t26 = viewMode === "category";
    let t27;
    if ($[62] !== t26) {
      t27 = <button type="button" onClick={t25} className="btn" aria-pressed={t26}>Content Categorization</button>;
      $[62] = t26;
      $[63] = t27;
    } else {
      t27 = $[63];
    }
    let t28;
    if ($[64] !== t24 || $[65] !== t27) {
      t28 = <div className="modes" role="group" aria-label="Visualization mode">{t24}{t27}</div>;
      $[64] = t24;
      $[65] = t27;
      $[66] = t28;
    } else {
      t28 = $[66];
    }
    let t29;
    if ($[67] !== viewMode) {
      t29 = viewMode === "sentiment" ? <ul className="legend" aria-label="Legend"><li><span className="swatch positive" aria-hidden="true" />Positive</li><li><span className="swatch neutral" aria-hidden="true" />Neutral</li><li><span className="swatch negative" aria-hidden="true" />Negative</li></ul> : <p className="hint">Click a category to filter the word cloud:</p>;
      $[67] = viewMode;
      $[68] = t29;
    } else {
      t29 = $[68];
    }
    let t30;
    if ($[69] !== t28 || $[70] !== t29) {
      t30 = <section className="sec tight bar">{t28}{t29}</section>;
      $[69] = t28;
      $[70] = t29;
      $[71] = t30;
    } else {
      t30 = $[71];
    }
    const t31 = viewMode === "sentiment";
    const t32 = viewMode === "category";
    let t33;
    if ($[72] !== canvas.height || $[73] !== canvas.width || $[74] !== selectedCategory || $[75] !== t31 || $[76] !== t32 || $[77] !== wordFrequencies) {
      t33 = <WordCloudVisualization words={wordFrequencies} width={canvas.width} height={canvas.height} useSentiment={t31} useCategory={t32} filterCategory={selectedCategory} />;
      $[72] = canvas.height;
      $[73] = canvas.width;
      $[74] = selectedCategory;
      $[75] = t31;
      $[76] = t32;
      $[77] = wordFrequencies;
      $[78] = t33;
    } else {
      t33 = $[78];
    }
    const t34 = viewMode === "sentiment" ? "Word Cloud with Sentiment Analysis" : "Word Cloud by Content Category";
    const t35 = !isNarrow && " \xB7 hover a word for its count";
    let t36;
    if ($[79] !== t34 || $[80] !== t35) {
      t36 = <figcaption className="k">{t34}{" "}· sized by frequency{t35}</figcaption>;
      $[79] = t34;
      $[80] = t35;
      $[81] = t36;
    } else {
      t36 = $[81];
    }
    let t37;
    if ($[82] !== t33 || $[83] !== t36) {
      t37 = <figure className="wc">{t33}{t36}</figure>;
      $[82] = t33;
      $[83] = t36;
      $[84] = t37;
    } else {
      t37 = $[84];
    }
    let t38;
    if ($[85] !== sentiments || $[86] !== viewMode) {
      t38 = viewMode === "sentiment" && <section className="sec"><div className="k">01 — Sentiment</div><h2>Sentiment Breakdown</h2><div className="figures">{sentiments.map(_temp4)}</div></section>;
      $[85] = sentiments;
      $[86] = viewMode;
      $[87] = t38;
    } else {
      t38 = $[87];
    }
    t2 = <>{t21}{t30}{viewMode === "category" && <div className="chips cats" role="group" aria-label="Filter by category"><button type="button" onClick={() => setSelectedCategory(null)} className={selectedCategory === null ? "chip on" : "chip"} aria-pressed={selectedCategory === null}>All Categories</button>{categoryStats.map(stat => <button key={stat.category} type="button" onClick={() => setSelectedCategory(stat.category)} className={selectedCategory === stat.category ? "chip on" : "chip"} aria-pressed={selectedCategory === stat.category}><span className={`swatch ${stat.category}`} aria-hidden="true" />{CATEGORY_LABELS[stat.category]} ({stat.count})</button>)}</div>}{t37}{t38}{viewMode === "category" && <section className="sec"><div className="k">01 — Categories</div><h2>Category Distribution</h2><ul className="dist">{categoryStats.map(stat_0 => <li key={stat_0.category}><button type="button" onClick={() => setSelectedCategory(stat_0.category)} aria-pressed={selectedCategory === stat_0.category}><span className="name"><span className={`swatch ${stat_0.category}`} aria-hidden="true" />{CATEGORY_LABELS[stat_0.category]}</span><span className="track" aria-hidden="true"><span className={`fill ${stat_0.category}`} style={{
                  width: `${stat_0.count / maxCategoryCount * 100}%`
                }} /></span><span className="sub">{stat_0.count} words · {stat_0.percentage.toFixed(1)}% of words · {stat_0.totalOccurrences} total occurrences</span></button></li>)}</ul></section>}</>;
    $[2] = canvas.height;
    $[3] = canvas.width;
    $[4] = isNarrow;
    $[5] = selectedCategory;
    $[6] = sentimentStats.negative;
    $[7] = sentimentStats.neutral;
    $[8] = sentimentStats.positive;
    $[9] = sentimentStats.totalNegative;
    $[10] = sentimentStats.totalNeutral;
    $[11] = sentimentStats.totalPositive;
    $[12] = totalPosts;
    $[13] = viewMode;
    $[14] = wordFrequencies;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}
function _temp4(item) {
  return <div key={item.key}><div className="label"><span className={`swatch ${item.key}`} aria-hidden="true" /><span className="k">{item.label}</span></div><div className="num">{item.count}</div><div className="sub">Total occurrences: {item.total}</div></div>;
}
function _temp3(s) {
  return s.count;
}
function _temp2() {
  return false;
}
function _temp() {
  return window.matchMedia(NARROW_QUERY).matches;
}